<script setup>
import { useApiRequest } from '@/composables/useApiRequest'
import { useApi } from '@/utils/api'

definePage({
  meta: {
    layout: 'default',
    action: 'update',
    subject: 'Product',
  },
})

const router = useRouter()
const route = useRoute()
const groupId = route.params.id

const { loading, errors, request } = useApiRequest()
const snackbar = ref({ show: false, text: '', color: 'success' })
const patchingModifierId = ref(null)
const pageLoading = ref(true)

const products = ref([])
const productsLoading = ref(false)

const form = ref({
  name: '',
  is_required: false,
  is_multiple: false,
  is_active: true,
  product_ids: [],
  modifiers: [],
})

// ── Load produk untuk dipilih ────────────────────────────────────────────
const loadProducts = async () => {
  productsLoading.value = true
  try {
    const res = await useApi('/api/v1/products', { query: { per_page: 200, is_active: true } }).get()
    const data = res?.data ?? res
    products.value = Array.isArray(data) ? data : (data?.data ?? [])
  } catch (err) {
    showSnackbar('Gagal memuat daftar produk', 'error')
  } finally {
    productsLoading.value = false
  }
}

// ── Load ───────────────────────────────────────────────────────────────────
const loadGroup = async () => {
  pageLoading.value = true
  try {
    const response = await useApi(`/api/v1/modifier-groups/${groupId}`).get()
    const data = response?.data ?? response
    const g = data?.data ?? data

    form.value = {
      name: g.name ?? '',
      is_required: g.is_required ?? false,
      is_multiple: g.is_multiple ?? false,
      is_active: g.is_active ?? true,
      product_ids: g.product_ids ?? (g.products ?? []).map(p => p.id),
      modifiers: (g.modifiers ?? []).map(m => ({ ...m })),
    }
  } catch (err) {
    showSnackbar('Gagal memuat data', 'error')
  } finally {
    pageLoading.value = false
  }
}

onMounted(() => {
  loadProducts()
  loadGroup()
})

// ── Modifier list ops ──────────────────────────────────────────────────────
const addModifier = () => {
  form.value.modifiers.push({ id: null, name: '', price_adjustment: 0, is_active: true })
}

const removeModifier = idx => {
  form.value.modifiers.splice(idx, 1)
}

// ── Submit all (PUT replace-all) ───────────────────────────────────────────
const submit = async () => {
  const res = await request(() =>
    useApi(`/api/v1/modifier-groups/${groupId}`).put({ body: form.value })
  )
  
  if (res) {
    showSnackbar('Berhasil diperbarui')
    router.push({ name: 'catalog-modifier-groups' })
  }
}

// ─ Patch single modifier (PATCH /modifier-groups/{id}/modifiers/{mid}) ───
const patchModifier = async modifier => {
  if (!modifier.id) return
  patchingModifierId.value = modifier.id
  
  const res = await request(() =>
    useApi(`/api/v1/modifier-groups/${groupId}/modifiers/${modifier.id}`).patch({
      body: {
        name: modifier.name,
        price_adjustment: modifier.price_adjustment,
        is_active: modifier.is_active,
      },
    })
  )
  
  if (res) {
    showSnackbar(`Opsi "${modifier.name}" diperbarui`)
  }
  
  patchingModifierId.value = null
}

const showSnackbar = (text, color = 'success') => {
  snackbar.value = { show: true, text, color }
}

const breadcrumbs = [
  { title: 'Katalog', disabled: true },
  { title: 'Modifier Groups', to: { name: 'catalog-modifier-groups' } },
  { title: 'Edit Group', disabled: true },
]
</script>

<template>
  <div>
    <VBreadcrumbs :items="breadcrumbs" class="px-0 mb-4" />
    <div v-if="pageLoading" class="d-flex justify-center py-10">
      <VProgressCircular indeterminate color="primary" />
    </div>

    <template v-else>
      <VRow>
        <VCol cols="12" md="8">
          <!-- Group info -->
          <VCard class="mb-4">
            <VCardTitle class="pa-5 pb-0">Informasi Group</VCardTitle>
            <VCardText class="pt-4">
              <VTextField
                v-model="form.name"
                label="Nama Group *"
                class="mb-4"
                :error-messages="errors['name']"
              />

              <VRow>
                <VCol cols="12" sm="6">
                  <div class="d-flex align-center justify-space-between pa-3 rounded-lg bg-grey-lighten-4">
                    <div>
                      <div class="text-body-2 font-weight-medium">Wajib Dipilih</div>
                      <div class="text-caption text-medium-emphasis">Pelanggan harus memilih</div>
                    </div>
                    <VSwitch v-model="form.is_required" hide-details color="warning" />
                  </div>
                </VCol>
                <VCol cols="12" sm="6">
                  <div class="d-flex align-center justify-space-between pa-3 rounded-lg bg-grey-lighten-4">
                    <div>
                      <div class="text-body-2 font-weight-medium">Bisa Multi-pilih</div>
                      <div class="text-caption text-medium-emphasis">Boleh pilih lebih dari satu</div>
                    </div>
                    <VSwitch v-model="form.is_multiple" hide-details color="info" />
                  </div>
                </VCol>
              </VRow>
            </VCardText>
          </VCard>

          <!-- Produk terkait -->
          <VCard class="mb-4">
            <VCardTitle class="pa-5 pb-0">Terapkan ke Produk</VCardTitle>
            <VCardText class="pt-4">
              <VAutocomplete
                v-model="form.product_ids"
                :items="products"
                :loading="productsLoading"
                item-title="name"
                item-value="id"
                label="Pilih Produk"
                placeholder="Pilih satu atau beberapa produk"
                multiple
                chips
                closable-chips
                hint="Modifier group ini akan muncul di POS saat produk yang dipilih ditambahkan ke cart"
                persistent-hint
                :error-messages="errors['product_ids']"
              />
            </VCardText>
          </VCard>

          <!-- Modifiers -->
          <VCard>
            <VCardTitle class="pa-5 pb-0 d-flex align-center justify-space-between">
              <span>Daftar Opsi</span>
              <VBtn size="small" variant="tonal" prepend-icon="ri-add-line" @click="addModifier">
                Tambah Opsi
              </VBtn>
            </VCardTitle>
            <VCardText class="pt-2">
              <VAlert type="info" variant="tonal" density="compact" class="mb-4">
                Gunakan <strong>ikon simpan</strong> untuk update satu opsi,
                atau <strong>Simpan Semua</strong> untuk replace seluruh daftar.
              </VAlert>

              <div v-if="!form.modifiers.length" class="text-center py-6">
                <p class="text-body-2 text-medium-emphasis">Belum ada opsi modifier</p>
              </div>

              <div
                v-for="(modifier, idx) in form.modifiers"
                :key="modifier.id ?? idx"
              >
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="d-flex align-center gap-2">
                    <span class="text-caption text-medium-emphasis">Opsi {{ idx + 1 }}</span>
                    <VChip v-if="modifier.id" size="x-small" variant="outlined">
                      ID: {{ modifier.id }}
                    </VChip>
                  </div>
                  <div class="d-flex gap-1">
                    <VBtn
                      v-if="modifier.id"
                      icon
                      variant="text"
                      size="x-small"
                      color="primary"
                      :loading="patchingModifierId === modifier.id"
                      @click="patchModifier(modifier)"
                    >
                      <VIcon icon="ri-save-line" />
                      <VTooltip activator="parent">Simpan opsi ini saja</VTooltip>
                    </VBtn>
                    <VBtn
                      icon
                      variant="text"
                      size="x-small"
                      color="error"
                      @click="removeModifier(idx)"
                    >
                      <VIcon icon="ri-delete-bin-line" />
                    </VBtn>
                  </div>
                </div>

                <VRow dense>
                  <VCol cols="12" sm="7">
                    <VTextField
                      v-model="modifier.name"
                      label="Nama Opsi *"
                      :error-messages="errors[`modifiers.${idx}.name`]"
                    />
                  </VCol>
                  <VCol cols="8" sm="3">
                    <VTextField
                      v-model.number="modifier.price_adjustment"
                      label="Penyesuaian Harga"
                      type="number"
                      prefix="Rp"
                      :error-messages="errors[`modifiers.${idx}.price_adjustment`]"
                    />
                  </VCol>
                  <VCol cols="4" sm="2" class="d-flex align-center">
                    <VSwitch
                      v-model="modifier.is_active"
                      hide-details
                      color="success"
                      density="compact"
                    />
                  </VCol>
                </VRow>

                <VDivider v-if="idx < form.modifiers.length - 1" class="my-3" />
              </div>
            </VCardText>
          </VCard>
        </VCol>

        <VCol cols="12" md="4">
          <VCard>
            <VCardTitle class="pa-5 pb-0">Status</VCardTitle>
            <VCardText class="pt-4">
              <div class="d-flex align-center justify-space-between">
                <span class="text-body-2">Group Aktif</span>
                <VSwitch v-model="form.is_active" hide-details color="success" />
              </div>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>

      <VRow class="mt-2">
        <VCol cols="12" class="d-flex gap-3 justify-end">
          <VBtn variant="tonal" :to="{ name: 'catalog-modifier-groups' }">Batal</VBtn>
          <VBtn :loading="loading" @click="submit">Simpan Semua</VBtn>
        </VCol>
      </VRow>
    </template>

    <VSnackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.text }}
    </VSnackbar>
  </div>
</template>
